import { useCallback, useState } from 'react';

/** The drawing paper's size on screen, in result cards (the mosaic's 16:9 grid unit). */
export interface PaperSpan {
  readonly columns: number;
  readonly rows: number;
}

export const DEFAULT_PAPER_SPAN: PaperSpan = { columns: 2, rows: 2 };
export const MAX_PAPER_ROWS = 8;
const OVERSIZED_PAPER_SPAN: PaperSpan = { columns: 3, rows: 3 };
const STORAGE_KEY = 'peruselab.paperSpan';

export function clampPaperSpan(span: PaperSpan, maxColumns: number): PaperSpan {
  const columns = Math.round(Math.min(Math.max(span.columns, 1), Math.max(maxColumns, 1)));
  const rows = Math.round(Math.min(Math.max(span.rows, 1), MAX_PAPER_ROWS));
  return { columns, rows };
}

function readStoredSpan(): PaperSpan {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PAPER_SPAN;
    const parsed = JSON.parse(raw) as Partial<PaperSpan>;
    if (typeof parsed.columns !== 'number' || typeof parsed.rows !== 'number') return DEFAULT_PAPER_SPAN;
    return clampPaperSpan({ columns: parsed.columns, rows: parsed.rows }, Number.POSITIVE_INFINITY);
  } catch {
    return DEFAULT_PAPER_SPAN;
  }
}

function storeSpan(span: PaperSpan) {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(span));
  } catch {
    // Private mode or blocked storage: the size just isn't remembered.
  }
}

/**
 * #737 -- the paper's on-screen size, resized from its corner one result card at a time and
 * remembered per device (a view preference, like the grid toggles; never part of the drawing or
 * its Undo history). An oversized import still grows it automatically to at least 3x3 (the old
 * fixed oversized span), and the user can shrink it again afterwards.
 */
export function usePaperSpan(isOversizedImport: boolean) {
  const [span, setSpan] = useState<PaperSpan>(readStoredSpan);

  // Adjusting state when a prop changes, during render (React's documented pattern, no effect).
  const [wasOversized, setWasOversized] = useState(isOversizedImport);
  if (isOversizedImport !== wasOversized) {
    setWasOversized(isOversizedImport);
    if (isOversizedImport)
      setSpan((current) => ({
        columns: Math.max(current.columns, OVERSIZED_PAPER_SPAN.columns),
        rows: Math.max(current.rows, OVERSIZED_PAPER_SPAN.rows)
      }));
  }

  /** `commit` (end of a drag, a key press, a reset) also remembers the size on this device. */
  const changeSpan = useCallback((next: PaperSpan, commit: boolean) => {
    setSpan((current) => (current.columns === next.columns && current.rows === next.rows ? current : next));
    if (commit) storeSpan(next);
  }, []);

  return { span, changeSpan };
}
